/**
 * Interactive splash link syntax:
 *   Try [mode.normal]Normal mode
 *   Try [mode.colorrush]Color_Rush   ← `_` becomes a space; whole phrase is one link
 *   You can disable this [settings.homesplashtexts]HERE!
 *
 * After `]`, the next run of non-whitespace is the clickable label (underlined).
 * Underscores in the label render as spaces so multi-word labels stay one pressable link.
 */

export type SplashSegment =
  | { type: "text"; value: string }
  | { type: "link"; target: string; label: string };

export type SplashLinkTarget = {
  id: string;
  label: string;
  kind: "mode" | "settings" | "screen" | "hub";
  /** Settings accordion element id (settings.* only). */
  accordionId?: string;
  /** Optional element to highlight after navigation. */
  highlightSelector?: string;
};

/** Mode ids that appear on the Modes screen (`data-mode-id`). */
export const SPLASH_MODE_IDS: Array<{ id: string; title: string }> = [
  { id: "normal", title: "Normal" },
  { id: "progression", title: "Progression" },
  { id: "wasd", title: "WASD" },
  { id: "superdot", title: "Superdot" },
  { id: "roulette", title: "Roulette" },
  { id: "speedrun", title: "SpeedRun" },
  { id: "penalty", title: "Golf" },
  { id: "highway", title: "Highway" },
  { id: "darkness", title: "Darkness" },
  { id: "mirror", title: "Mirror" },
  { id: "snake", title: "Snake" },
  { id: "combo", title: "Combo" },
  { id: "colorrush", title: "Color Rush" },
  { id: "heat", title: "Heat" },
  { id: "hunter", title: "Hunter" },
  { id: "pop", title: "POP" },
  { id: "counter", title: "Counter" },
  { id: "aim", title: "AIM" },
  { id: "hook", title: "Hook" },
  { id: "tilt", title: "Tilt" },
  { id: "orbit", title: "Orbit" },
  { id: "delay", title: "Delay" },
];

const ACC_A11Y = "settingsAccessibilityAccordion";
const ACC_HOME = "settingsHomeAccordion";
const ACC_APPEAR = "settingsAppearanceAccordion";
const ACC_SOUND = "settingsSoundAccordion";
const ACC_KEYS = "keybindsSettingsAccordion";
const ACC_PUSH = "settingsNotificationsAccordion";
const ACC_ACCOUNT = "settingsAccountAccordion";

/** Every Settings card linkable from splash autocomplete. */
const SETTINGS_TARGETS: SplashLinkTarget[] = [
  // Accessibility — Gameplay
  { id: "settings.autoretry", label: "Settings — Auto Retry", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.quickrestart", label: "Settings — Quick restart", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.wasdsensitivity", label: "Settings — WASD Sensitivity", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.modeselectpanel", label: "Settings — Mode select panel", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.playtransition", label: "Settings — Play transition", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.keybindindicator", label: "Settings — Show keybind indicator", kind: "settings", accordionId: ACC_A11Y },
  // Accessibility — Visual effects
  { id: "settings.flashes", label: "Settings — Flashes", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.shake", label: "Settings — Shake", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.loseanimation", label: "Settings — Lose animation", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.homeambientballs", label: "Settings — Home background balls", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.showfps", label: "Settings — Show FPS", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.framerate", label: "Settings — Frame rate", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.lowpowercap", label: "Settings — Low Power cap (60 FPS)", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.invertreturnpause", label: "Settings — Invert Return and Pause", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.holdreturnhome", label: "Settings — Hold Return for Home", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.invertsidebar", label: "Settings — Invert Sidebar", kind: "settings", accordionId: ACC_A11Y },
  // Home
  { id: "settings.newsinhome", label: "Settings — Show News", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.notificationsinhome", label: "Settings — Show Notifications", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homesplashtexts", label: "Settings — Show Splash Texts", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowfriends", label: "Settings — Show Friends", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowlastmode", label: "Settings — Show Last Mode Played", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homecursorbounce", label: "Settings — Cursor Bounce", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowtrending", label: "Settings — Show Trending Mode", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowdots", label: "Settings — Show Dots", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowstreak", label: "Settings — Show Streak", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowchallenge", label: "Settings — Show Challenge Status", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowlootdrops", label: "Settings — Show Active LootDrops", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshownewinshop", label: "Settings — Show New in Shop", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowsuggested", label: "Settings — Show Suggested Mode", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homeshowcontinue", label: "Settings — Show Continue Playing", kind: "settings", accordionId: ACC_HOME },
  { id: "settings.homecustomizelayout", label: "Settings — Customize Layout", kind: "settings", accordionId: ACC_HOME },
  // Accessibility — Mobile
  { id: "settings.dpad", label: "Settings — DPad", kind: "settings", accordionId: ACC_A11Y },
  { id: "settings.joystick", label: "Settings — Joystick", kind: "settings", accordionId: ACC_A11Y },
  // Appearance — Colours
  { id: "settings.ballscolor", label: "Settings — Balls Color", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.cursorcolor", label: "Settings — Cursor Color", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.buttonscolor", label: "Settings — Buttons Color", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.textcolor", label: "Settings — Text Color", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.trailcolor", label: "Settings — Trail Color", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.menubackground", label: "Settings — Menu Background", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.gamebackground", label: "Settings — Game Background", kind: "settings", accordionId: ACC_APPEAR },
  // Appearance — Display
  { id: "settings.trail", label: "Settings — Trail", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.cosmeticsmatchcursor", label: "Settings — Cosmetics match cursor", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.adapttextcolor", label: "Settings — Adapt text color", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.menubright", label: "Settings — Menu bright", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.gamebright", label: "Settings — Game bright", kind: "settings", accordionId: ACC_APPEAR },
  { id: "settings.neonballs", label: "Settings — Neon Balls", kind: "settings", accordionId: ACC_APPEAR },
  // Sound
  { id: "settings.music", label: "Settings — Music", kind: "settings", accordionId: ACC_SOUND },
  { id: "settings.uivolume", label: "Settings — UI Volume", kind: "settings", accordionId: ACC_SOUND },
  { id: "settings.bouncesfx", label: "Settings — Bounce SFX Volume", kind: "settings", accordionId: ACC_SOUND },
  { id: "settings.pickupsfx", label: "Settings — Pickup SFX Volume", kind: "settings", accordionId: ACC_SOUND },
  { id: "settings.gamesfx", label: "Settings — Game SFX Volume", kind: "settings", accordionId: ACC_SOUND },
  { id: "settings.darknesssfx", label: "Settings — Darkness SFX Volume", kind: "settings", accordionId: ACC_SOUND },
  { id: "settings.sounds", label: "Settings — Sounds", kind: "settings", accordionId: ACC_SOUND },
  // Keybinds
  { id: "settings.keybindreturn", label: "Settings — Keybind Return", kind: "settings", accordionId: ACC_KEYS },
  { id: "settings.keybindpause", label: "Settings — Keybind Pause", kind: "settings", accordionId: ACC_KEYS },
  { id: "settings.keybindmenu", label: "Settings — Keybind Menu", kind: "settings", accordionId: ACC_KEYS },
  { id: "settings.keybindplayagain", label: "Settings — Keybind Play again / Start", kind: "settings", accordionId: ACC_KEYS },
  { id: "settings.keybindnextlevel", label: "Settings — Keybind Next Level", kind: "settings", accordionId: ACC_KEYS },
  { id: "settings.keybindquickrestart", label: "Settings — Keybind Quick restart", kind: "settings", accordionId: ACC_KEYS },
  { id: "settings.keybinduicursor", label: "Settings — Keybind UI cursor", kind: "settings", accordionId: ACC_KEYS },
  // Notifications
  { id: "settings.pushenable", label: "Settings — Enable push notifications", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushfriends", label: "Settings — Push Friends", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushmessages", label: "Settings — Push Messages", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushmoderation", label: "Settings — Push Moderation", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushannouncements", label: "Settings — Push Announcements", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushshop", label: "Settings — Push Shop", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushevents", label: "Settings — Push Events", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushcrates", label: "Settings — Push Crates", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushplaystreak", label: "Settings — Push Play streak", kind: "settings", accordionId: ACC_PUSH },
  { id: "settings.pushdailyreward", label: "Settings — Push Daily reward", kind: "settings", accordionId: ACC_PUSH },
  // Account
  { id: "settings.account", label: "Settings — Account (sign in)", kind: "settings", accordionId: ACC_ACCOUNT },
  { id: "settings.session", label: "Settings — Session", kind: "settings", accordionId: ACC_ACCOUNT },
  { id: "settings.changepassword", label: "Settings — Change Password", kind: "settings", accordionId: ACC_ACCOUNT },
  { id: "settings.changeusername", label: "Settings — Change Username", kind: "settings", accordionId: ACC_ACCOUNT },
];

/** Screens, Play hub categories, and other top-level destinations. */
const SCREEN_TARGETS: SplashLinkTarget[] = [
  { id: "screen.home", label: "Screen — Home", kind: "screen", highlightSelector: "#startScreen .homeTitle" },
  { id: "screen.play", label: "Screen — Play hub", kind: "screen", highlightSelector: "#gameHubGrid" },
  { id: "screen.settings", label: "Screen — Settings", kind: "screen", highlightSelector: "#settingsScreen h1" },
  { id: "screen.rankings", label: "Screen — Rankings", kind: "screen", highlightSelector: "#leaderboardScreen h1" },
  { id: "screen.stats", label: "Screen — Stats", kind: "screen", highlightSelector: "#statsScreen h1" },
  { id: "screen.achievements", label: "Screen — Achievements", kind: "screen", highlightSelector: "#achievementsScreen h1" },
  { id: "screen.shop", label: "Screen — Shop", kind: "screen", highlightSelector: "#shopScreen h1" },
  { id: "screen.inventory", label: "Screen — Inventory", kind: "screen", highlightSelector: "#inventoryScreen h1" },
  { id: "screen.social", label: "Screen — Social", kind: "screen", highlightSelector: "#profileScreen h1" },
  { id: "screen.news", label: "Screen — News", kind: "screen", highlightSelector: "#newsScreen h1, #newsScreen .newsScreenTitle" },
  { id: "screen.notifications", label: "Screen — Notifications", kind: "screen", highlightSelector: "#notificationsScreen h1" },
  { id: "screen.wall", label: "Screen — Wall", kind: "screen", highlightSelector: "#wallScreen h1" },
  { id: "screen.credits", label: "Screen — Credits", kind: "screen", highlightSelector: "#creditsScreen h1" },
  { id: "screen.modes", label: "Screen — Modes", kind: "screen", highlightSelector: "#modeScreen h1" },
  { id: "screen.practice", label: "Screen — Practice", kind: "screen", highlightSelector: "#practiceScreen h1" },
  { id: "screen.sandbox", label: "Screen — Sandbox", kind: "screen", highlightSelector: "#sandboxBaseScreen h1, #sandboxBaseScreen" },
  { id: "screen.multiplayer", label: "Screen — Multiplayer", kind: "screen", highlightSelector: "#mpEntryScreen h1, #mpEntryScreen" },
  { id: "screen.local", label: "Screen — Local", kind: "screen", highlightSelector: "#localSetupScreen h1, #localSetupScreen" },
  { id: "screen.lootdrops", label: "Screen — Lootdrops", kind: "screen", highlightSelector: "#ldListScreen h1, #ldListScreen" },
  { id: "screen.challenges", label: "Screen — Challenges", kind: "screen", highlightSelector: "#challengesScreen h1, #challengesScreen" },
  // Hub aliases (same destinations; shown under Hub — in autocomplete)
  { id: "hub.modes", label: "Hub — Modes", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="modes"]' },
  { id: "hub.practice", label: "Hub — Practice", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="practice"]' },
  { id: "hub.sandbox", label: "Hub — Sandbox", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="sandbox"]' },
  { id: "hub.multiplayer", label: "Hub — Multiplayer", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="multiplayer"]' },
  { id: "hub.local", label: "Hub — Local", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="local"]' },
  { id: "hub.lootdrops", label: "Hub — Lootdrops", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="lootdrops"]' },
  { id: "hub.challenges", label: "Hub — Challenges", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="challenges"]' },
  { id: "hub.campaign", label: "Hub — Campaign", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="campaign"]' },
  { id: "hub.tournaments", label: "Hub — Tournaments", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="tournaments"]' },
  { id: "hub.marathon", label: "Hub — Marathon", kind: "hub", highlightSelector: '#gameHubGrid [data-hub-category="marathon"]' },
  { id: "hub.tab.play", label: "Hub section — Play", kind: "hub", highlightSelector: "#gameHubSectionTitlePlay" },
  { id: "hub.tab.quests", label: "Hub section — Quests", kind: "hub", highlightSelector: "#gameHubSectionTitleQuests" },
  { id: "hub.soccer", label: "Hub — Cursor Soccer", kind: "hub", highlightSelector: '#gameHubGridCompete [data-hub-category="soccer"]' },
  { id: "hub.tab.compete", label: "Hub section — Compete", kind: "hub", highlightSelector: "#gameHubSectionTitleCompete" },
];

const LINK_RE = /\[([a-z][a-z0-9]*(?:\.[a-z0-9_-]+)*)\](\S+)/gi;

/** Underscores in link labels become spaces so `Color_Rush` shows as one underlined "Color Rush". */
export function formatSplashLinkLabel(label: string): string {
  return String(label || "").replace(/_/g, " ");
}

export function listSplashLinkTargets(): SplashLinkTarget[] {
  const modes: SplashLinkTarget[] = SPLASH_MODE_IDS.map((m) => ({
    id: `mode.${m.id}`,
    label: `Mode — ${m.title}`,
    kind: "mode" as const,
  }));
  return [...modes, ...SETTINGS_TARGETS, ...SCREEN_TARGETS];
}

export function getSplashSettingsTarget(target: string): SplashLinkTarget | null {
  const t = String(target || "").trim().toLowerCase();
  if (!t.startsWith("settings.")) return null;
  return SETTINGS_TARGETS.find((x) => x.id === t) ?? null;
}

export function getSplashScreenTarget(target: string): SplashLinkTarget | null {
  const t = String(target || "").trim().toLowerCase();
  if (!t.startsWith("screen.") && !t.startsWith("hub.")) return null;
  return SCREEN_TARGETS.find((x) => x.id === t) ?? null;
}

export function isKnownSplashLinkTarget(target: string): boolean {
  const t = String(target || "").trim().toLowerCase();
  if (!t) return false;
  return listSplashLinkTargets().some((x) => x.id === t);
}

export function parseSplashLine(raw: string): SplashSegment[] {
  const s = String(raw || "");
  if (!s) return [];
  const out: SplashSegment[] = [];
  let last = 0;
  LINK_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = LINK_RE.exec(s))) {
    const start = m.index;
    if (start > last) out.push({ type: "text", value: s.slice(last, start) });
    out.push({ type: "link", target: m[1].toLowerCase(), label: formatSplashLinkLabel(m[2]) });
    last = start + m[0].length;
  }
  if (last < s.length) out.push({ type: "text", value: s.slice(last) });
  return out.length ? out : [{ type: "text", value: s }];
}

/** Visible characters (link labels, not the `[target]` markup). */
export function splashPlainText(raw: string): string {
  return parseSplashLine(raw)
    .map((seg) => (seg.type === "text" ? seg.value : seg.label))
    .join("");
}

/**
 * Incomplete `[partial` at cursor — returns the typed fragment after `[` (may be empty).
 * null if not currently inside an open bracket token.
 */
export function splashAutocompleteQuery(text: string, cursor: number): { start: number; query: string } | null {
  const before = text.slice(0, Math.max(0, cursor));
  const open = before.lastIndexOf("[");
  if (open < 0) return null;
  const afterOpen = before.slice(open + 1);
  if (afterOpen.includes("]")) return null;
  if (/[\s\n]/.test(afterOpen)) return null;
  return { start: open + 1, query: afterOpen };
}

export function filterSplashLinkTargets(query: string): SplashLinkTarget[] {
  const q = String(query || "").trim().toLowerCase();
  const all = listSplashLinkTargets();
  if (!q) return all;
  return all.filter((t) => t.id.includes(q) || t.label.toLowerCase().includes(q));
}
